import type { DefectDetail as DefectDetailDto, DefectStatus, QuestionSeverity } from '@my/check-api';
import { Button, SelectInput, TextInput } from '@my/ui';
import { Clock, ExternalLink, Send, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { SEVERITY_BADGE, STATUS_BADGE, STATUS_ORDER } from './lib/defectTypes';
import { useWhenLabel } from './useWhenLabel';
import { AnswerPhoto } from '../checks/components/CheckAttachments';

interface DefectDetailProps {
  defect: DefectDetailDto | null;
  isSaving: boolean;
  severityLabels: Record<QuestionSeverity, string>;
  statusLabels: Record<DefectStatus, string>;
  onRecordAction: (input: {
    note: string;
    statusAfter: DefectStatus;
    garageName: string | null;
    dueUtc: string | null;
  }) => void;
}

/**
 * Right-hand detail pane: the defect header, where it came from, its
 * append-only action trail, and the compose box for the next action.
 *
 * Recording an action is the only way status, garage or due date changes —
 * there is no edit-defect endpoint. That keeps the trail complete by
 * construction, which is what makes it usable as evidence.
 */
export function DefectDetail({
  defect,
  isSaving,
  severityLabels,
  statusLabels,
  onRecordAction,
}: DefectDetailProps) {
  const { t } = useTranslation('checks');
  const navigate = useNavigate();
  const whenLabel = useWhenLabel();

  const [note, setNote] = useState('');
  const [status, setStatus] = useState<DefectStatus>('Open');
  const [garage, setGarage] = useState('');
  const [due, setDue] = useState('');

  const summary = defect?.summary;

  // Re-seed whenever the selected defect changes, so the status dropdown
  // starts where the defect actually is and a half-typed note doesn't follow
  // the operator to a different card.
  useEffect(() => {
    setNote('');
    setStatus(summary?.status ?? 'Open');
    setGarage(summary?.garageName ?? '');
    setDue(summary?.dueUtc ? summary.dueUtc.slice(0, 10) : '');
  }, [summary?.id, summary?.status, summary?.garageName, summary?.dueUtc]);

  if (!defect || !summary) {
    return (
      <div className="flex h-full items-center justify-center rounded-sm border border-slate-300 bg-white shadow-sm">
        <p className="text-xs italic text-slate-400">{t('defects.detail.empty')}</p>
      </div>
    );
  }

  const canSave = note.trim().length > 0 && !isSaving;

  const submit = () => {
    if (!canSave) return;
    onRecordAction({
      note: note.trim(),
      statusAfter: status,
      garageName: garage.trim() || null,
      // A date input gives a local calendar day; send it as UTC midnight so
      // the deadline does not drift a day either way across time zones.
      dueUtc: due ? new Date(`${due}T00:00:00Z`).toISOString() : null,
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col rounded-sm border border-slate-300 bg-white shadow-sm">
      <header className="border-b border-slate-200 px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-slate-700">
            {summary.resourceRegistration}
          </span>
          <Badge className={SEVERITY_BADGE[summary.severity]}>
            {severityLabels[summary.severity]}
          </Badge>
          <Badge className={STATUS_BADGE[summary.status]}>{statusLabels[summary.status]}</Badge>
          {summary.isOverdue && (
            <Badge className="border-red-500 bg-red-500 text-white">
              {t('defects.overdue')}
            </Badge>
          )}
        </div>

        <h2 className="mt-2 text-base font-semibold text-slate-900">{summary.title}</h2>
        {summary.description && (
          <p className="mt-0.5 text-sm text-slate-600">{summary.description}</p>
        )}
        {/* The driver's photo is the evidence the office acts on — here, where
            the defect is worked, not only on the check it came from. */}
        {summary.hasPhoto && summary.checkId && summary.checkAnswerId && (
          <AnswerPhoto checkId={summary.checkId} answerId={summary.checkAnswerId} />
        )}
        <p className="mt-1 text-[10px] text-slate-500">
          {t('defects.detail.reportedBy', {
            name: summary.reportedByName,
            when: whenLabel(summary.reportedUtc, false),
          })}
          {summary.dueUtc && ` · ${t('defects.detail.due', {
            when: new Date(summary.dueUtc).toLocaleDateString(),
          })}`}
        </p>

        {/* Provenance. A defect that came from a check should be traceable back
            to the exact question that failed — that link is the reason
            CheckAnswerId exists. */}
        {defect.sourceQuestionLabel && (
          <div className="mt-2 rounded-sm border border-slate-200 bg-slate-50 px-2.5 py-2">
            <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
              {t('defects.detail.raisedBy')}
            </p>
            <p className="mt-0.5 text-xs text-slate-800">{defect.sourceQuestionLabel}</p>
            {defect.sourceNote && (
              <p className="mt-0.5 text-[11px] italic text-slate-600">“{defect.sourceNote}”</p>
            )}
            {summary.checkId && (
              <button
                type="button"
                onClick={() => navigate(`/checks/${summary.checkId}`)}
                className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-blue-600 hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                {t('defects.detail.viewCheck')}
              </button>
            )}
          </div>
        )}
      </header>

      {/* ── Action trail ────────────────────────────────────────────────── */}
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        <p className="text-xs font-medium text-slate-600">{t('defects.detail.actionHistory')}</p>
        {defect.actions.length === 0 ? (
          <p className="mt-2 text-[10px] italic text-slate-400">
            {t('defects.detail.noActions')}
          </p>
        ) : (
          <ol className="mt-2 space-y-2">
            {defect.actions.map((action) => (
              <li key={action.id} className="flex gap-2 rounded-sm bg-slate-50 px-2.5 py-2">
                <Clock
                  className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p className="text-sm text-slate-800">{action.note}</p>
                  <p className="mt-0.5 text-[10px] text-slate-500">
                    {action.recordedBy} · {whenLabel(action.recordedUtc, true)} ·{' '}
                    {statusLabels[action.statusAfter]}
                    {action.garageName && ` · ${action.garageName}`}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* ── Compose ─────────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 bg-slate-50 px-3 py-3">
        <p className="text-xs font-medium text-slate-600">{t('defects.record.title')}</p>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={1024}
          placeholder={t('defects.record.notePlaceholder')}
          className="mt-2 w-full resize-y rounded-sm border border-slate-300 bg-white px-2 py-2 text-sm font-semibold text-slate-900 placeholder:text-xs placeholder:font-normal placeholder:italic placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[11rem_1fr_9rem_auto] sm:items-center">
          <SelectInput
            label={t('defects.record.statusLabel')}
            name="defect-status"
            value={status}
            options={STATUS_ORDER.map((s) => ({ value: s, label: statusLabels[s] }))}
            onChange={(e) => setStatus(e.target.value as DefectStatus)}
          />
          <TextInput
            name="defect-garage"
            value={garage}
            maxLength={128}
            aria-label={t('defects.record.garagePlaceholder')}
            placeholder={t('defects.record.garagePlaceholder')}
            onChange={(e) => setGarage(e.target.value)}
          />
          <TextInput
            name="defect-due"
            type="date"
            value={due}
            label={t('defects.record.dueLabel')}
            onChange={(e) => setDue(e.target.value)}
          />
          <Button leftIcon={Send} onClick={submit} disabled={!canSave}>
            {isSaving ? t('defects.record.saving') : t('defects.record.save')}
          </Button>
        </div>
        {!note.trim() && (
          <p className="mt-1.5 flex items-center gap-1 text-[10px] italic text-slate-500">
            <TriangleAlert className="h-3 w-3 text-amber-500" />
            {t('defects.record.noteRequired')}
          </p>
        )}
      </footer>
    </div>
  );
}

function Badge({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase ${className}`}
    >
      {children}
    </span>
  );
}
